/* eslint-disable react-refresh/only-export-components */
import { useState, useEffect, useRef } from 'react'
import { Search, X, ChevronDown, Calendar } from 'lucide-react'
import { format, subDays, startOfDay, endOfDay } from 'date-fns'
import type { AnalysisType, Severity } from '../types'

interface Pond {
  id: string
  name: string
}

export interface AIHistoryFilterState {
  search: string
  pondId: string | null
  analysisType: AnalysisType | null
  severities: Severity[]
  startDate: Date
  endDate: Date
}

interface AIHistoryFiltersProps {
  ponds: Pond[]
  filters: AIHistoryFilterState
  onChange: (filters: AIHistoryFilterState) => void
  resultCount: number
}

export const defaultFilters: AIHistoryFilterState = {
  search: '',
  pondId: null,
  analysisType: null,
  severities: ['critical', 'high', 'medium', 'low'],
  startDate: startOfDay(subDays(new Date(), 30)),
  endDate: endOfDay(new Date()),
}

const SEVERITY_OPTIONS: { value: Severity; label: string; activeClass: string }[] = [
  { value: 'critical', label: 'Critical', activeClass: 'bg-red-100 text-red-700 border-red-200' },
  { value: 'high', label: 'High', activeClass: 'bg-orange-100 text-orange-700 border-orange-200' },
  { value: 'medium', label: 'Medium', activeClass: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  { value: 'low', label: 'Low', activeClass: 'bg-green-100 text-green-700 border-green-200' },
]

const DATE_PRESETS = [
  { label: 'Last 7 days', days: 7 },
  { label: 'Last 14 days', days: 14 },
  { label: 'Last 30 days', days: 30 },
  { label: 'Last 90 days', days: 90 },
]

export function AIHistoryFilters({ ponds, filters, onChange, resultCount }: AIHistoryFiltersProps) {
  const [searchInput, setSearchInput] = useState(filters.search)
  const [showPondDropdown, setShowPondDropdown] = useState(false)
  const [showDateDropdown, setShowDateDropdown] = useState(false)
  const pondDropdownRef = useRef<HTMLDivElement>(null)
  const dateDropdownRef = useRef<HTMLDivElement>(null)

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (searchInput !== filters.search) {
        onChange({ ...filters, search: searchInput })
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [searchInput, filters, onChange])

  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (pondDropdownRef.current && !pondDropdownRef.current.contains(event.target as Node)) {
        setShowPondDropdown(false)
      }
      if (dateDropdownRef.current && !dateDropdownRef.current.contains(event.target as Node)) {
        setShowDateDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleSeverity = (severity: Severity) => {
    const newSeverities = filters.severities.includes(severity)
      ? filters.severities.filter(s => s !== severity)
      : [...filters.severities, severity]
    // Ensure at least one severity is selected
    if (newSeverities.length > 0) {
      onChange({ ...filters, severities: newSeverities })
    }
  }

  const handleDatePreset = (days: number) => {
    onChange({
      ...filters,
      startDate: startOfDay(subDays(new Date(), days)),
      endDate: endOfDay(new Date()),
    })
    setShowDateDropdown(false)
  }

  const handlePondSelect = (pondId: string | null) => {
    onChange({ ...filters, pondId })
    setShowPondDropdown(false)
  }

  const hasActiveFilters =
    filters.search ||
    filters.pondId ||
    filters.analysisType ||
    filters.severities.length < 4

  const selectedPond = ponds.find(p => p.id === filters.pondId)
  const currentDatePreset = DATE_PRESETS.find(preset => {
    const presetStart = startOfDay(subDays(new Date(), preset.days))
    return presetStart.getTime() === filters.startDate.getTime()
  })

  return (
    <div className="space-y-4">
      {/* Main filter row */}
      <div className="flex flex-wrap items-center gap-3 p-4 bg-white rounded-xl border border-slate-200/50 shadow-sm">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search analyses..."
            className="w-full pl-10 pr-8 py-2 rounded-lg border border-slate-200/50 bg-slate-50 text-sm placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-300 transition-colors"
          />
          {searchInput && (
            <button
              onClick={() => {
                setSearchInput('')
                onChange({ ...filters, search: '' })
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Pond filter */}
        <div className="relative" ref={pondDropdownRef}>
          <button
            onClick={() => setShowPondDropdown(!showPondDropdown)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200/50 bg-white text-sm hover:bg-slate-50 transition-colors min-w-[160px]"
          >
            <span className={selectedPond ? 'text-slate-900' : 'text-slate-500'}>
              {selectedPond?.name || 'All Ponds'}
            </span>
            <ChevronDown className="h-4 w-4 text-slate-400 ml-auto" />
          </button>
          {showPondDropdown && (
            <div className="absolute top-full left-0 mt-1 w-full min-w-[180px] bg-white rounded-lg border border-slate-200/50 shadow-lg z-20">
              <button
                onClick={() => handlePondSelect(null)}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors ${
                  !filters.pondId ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700'
                }`}
              >
                All Ponds
              </button>
              {ponds.map(pond => (
                <button
                  key={pond.id}
                  onClick={() => handlePondSelect(pond.id)}
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-slate-50 transition-colors ${
                    filters.pondId === pond.id ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700'
                  }`}
                >
                  {pond.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Analysis type toggle */}
        <div className="flex rounded-lg border border-slate-200/50 bg-slate-50 p-1">
          {[
            { value: null as AnalysisType | null, label: 'All' },
            { value: 'fish_diagnosis' as AnalysisType, label: 'Diagnosis' },
            { value: 'root_cause' as AnalysisType, label: 'Root Cause' },
          ].map(option => (
            <button
              key={option.label}
              onClick={() => onChange({ ...filters, analysisType: option.value })}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                filters.analysisType === option.value
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="relative" ref={dateDropdownRef}>
          <button
            onClick={() => setShowDateDropdown(!showDateDropdown)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200/50 bg-white text-sm hover:bg-slate-50 transition-colors"
          >
            <Calendar className="h-4 w-4 text-slate-400" />
            <span className="text-slate-700">
              {currentDatePreset?.label || `${format(filters.startDate, 'MMM d')} - ${format(filters.endDate, 'MMM d')}`}
            </span>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
          {showDateDropdown && (
            <div className="absolute top-full right-0 mt-1 bg-white rounded-lg border border-slate-200/50 shadow-lg z-20">
              {DATE_PRESETS.map(preset => (
                <button
                  key={preset.days}
                  onClick={() => handleDatePreset(preset.days)}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-slate-50 transition-colors whitespace-nowrap ${
                    currentDatePreset?.days === preset.days ? 'bg-emerald-50 text-emerald-700' : 'text-slate-700'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Severity pills row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xs font-medium text-slate-500 mr-2">Severity:</span>
          {SEVERITY_OPTIONS.map(option => (
            <button
              key={option.value}
              onClick={() => toggleSeverity(option.value)}
              className={`px-3 py-1 text-xs font-medium rounded-full border transition-all ${
                filters.severities.includes(option.value)
                  ? option.activeClass
                  : 'bg-slate-100 text-slate-400 border-slate-200 opacity-50'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-slate-500">
            {resultCount} {resultCount === 1 ? 'analysis' : 'analyses'}
          </span>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setSearchInput('')
                onChange(defaultFilters)
              }}
              className="text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

export default AIHistoryFilters
